import { Dispatch } from "redux";
import { baseAction } from "./baseAction";
import { pipelineConstant, loaderConstant } from "../constants";
import { pipelineService } from "../services/pipelineService";
import type { CreatePipelineRequest, PipelineWithStagesAndTags } from "../../containers/Pipelines/types";

const { request, success, failure } = baseAction;

// Get default pipeline (single pipeline mode - auto-creates if doesn't exist)
const getDefaultPipeline = (cb: (data?: any) => void = () => {}) => {
  return (dispatch: Dispatch) => {
    dispatch(request(pipelineConstant.GET_DEFAULT_PIPELINE_R, {}));
    dispatch(request(loaderConstant.LOADING_TRUE, {}));
    
    pipelineService.getDefaultPipeline().then(
      (data) => {
        dispatch(success(pipelineConstant.GET_DEFAULT_PIPELINE_S, data));
        dispatch(request(loaderConstant.LOADING_FALSE, {}));
        cb(data);
      },
      (error) => {
        dispatch(failure(pipelineConstant.GET_DEFAULT_PIPELINE_F, error));

        dispatch(request(loaderConstant.LOADING_FALSE, {}));
      }
    );
  };
};

// Update pipeline
const updatePipeline = (id: number, params: Partial<CreatePipelineRequest>, cb: (data?: any) => void = () => {}) => {
  return (dispatch: Dispatch) => {
    dispatch(request(pipelineConstant.UPDATE_PIPELINE_R, { id, ...params }));
    dispatch(request(loaderConstant.LOADING_TRUE, {}));
    
    pipelineService.updatePipeline(id, params).then(
      (data) => {
        dispatch(success(pipelineConstant.UPDATE_PIPELINE_S, data));

        dispatch(request(loaderConstant.LOADING_FALSE, {}));
        cb(data);
      },
      (error) => {
        dispatch(failure(pipelineConstant.UPDATE_PIPELINE_F, error));

        dispatch(request(loaderConstant.LOADING_FALSE, {}));
      }
    );
  };
};

// Delete pipeline
const deletePipeline = (id: number, cb: (data?: any) => void = () => {}) => {
  return (dispatch: Dispatch) => {
    dispatch(request(pipelineConstant.DELETE_PIPELINE_R, { id }));
    dispatch(request(loaderConstant.LOADING_TRUE, {}));
    
    pipelineService.deletePipeline(id).then(
      (data) => {
        dispatch(success(pipelineConstant.DELETE_PIPELINE_S, { id }));

        dispatch(request(loaderConstant.LOADING_FALSE, {}));
        cb(data);
      },
      (error) => {
        dispatch(failure(pipelineConstant.DELETE_PIPELINE_F, error));

        dispatch(request(loaderConstant.LOADING_FALSE, {}));
      }
    );
  };
};

// Set selected pipeline
const setSelectedPipeline = (pipeline: PipelineWithStagesAndTags | null) => {
  return (dispatch: Dispatch) => {
    dispatch(success(pipelineConstant.SET_SELECTED_PIPELINE, pipeline));
  };
};

const createStage = (pipelineId: number, stageData: { name: string; tag_name: string; order_index: number }, cb: (data?: any) => void = () => {}) => {
  return (dispatch: Dispatch) => {
    dispatch(request(pipelineConstant.CREATE_STAGE_R, stageData));

    pipelineService.createStage(pipelineId, stageData).then(
      (data) => {
        dispatch(success(pipelineConstant.CREATE_STAGE_S, data));
        cb(data);
      },
      (error) => {
        dispatch(failure(pipelineConstant.CREATE_STAGE_F, error));
      }
    );
  };
};

const updateStage = (pipelineId: number, stageData: { id: number; name: string; tag_name: string; order_index?: number }, cb: (data?: any) => void = () => {}) => {
  return (dispatch: Dispatch) => {
    dispatch(request(pipelineConstant.UPDATE_STAGE_R, stageData));

    pipelineService.updatePipeline(pipelineId, {
      stages: [stageData as any]
    }).then(
      (data) => {
        dispatch(success(pipelineConstant.UPDATE_STAGE_S, data));
        pipelineService.getDefaultPipeline().then((refreshedData) => {
          dispatch(success(pipelineConstant.GET_DEFAULT_PIPELINE_S, refreshedData));
          cb(data);
        });
      },
      (error) => {
        dispatch(failure(pipelineConstant.UPDATE_STAGE_F, error));
      }
    );
  };
};

const deleteStage = (pipelineId: number, stageId: number, cb: (data?: any) => void = () => {}) => {
  return (dispatch: Dispatch) => {
    dispatch(request(pipelineConstant.DELETE_STAGE_R, { stageId }));

    pipelineService.updatePipeline(pipelineId, {
      stages: [{ id: stageId, deleted: true } as any]
    }).then(
      (data) => {
        dispatch(success(pipelineConstant.DELETE_STAGE_S, { stageId }));
        pipelineService.getDefaultPipeline().then((refreshedData) => {
          dispatch(success(pipelineConstant.GET_DEFAULT_PIPELINE_S, refreshedData));
          cb(data);
        });
      },
      (error) => {
        dispatch(failure(pipelineConstant.DELETE_STAGE_F, error));
      }
    );
  };
};

const updateStagesSortOrders = (pipelineId: number, stages: Array<{ id: number; order_index: number }>, cb: (data?: any) => void = () => {}) => {
  return (dispatch: Dispatch) => {
    dispatch({
      type: pipelineConstant.REORDER_STAGES_OPTIMISTIC,
      payload: { stages }
    });

    pipelineService.reorderStages(pipelineId, stages).then(
      (data) => {
        cb(data);
      },
      (error) => {
        pipelineService.getDefaultPipeline().then((refreshedData) => {
          dispatch(success(pipelineConstant.GET_DEFAULT_PIPELINE_S, refreshedData));
        });
      }
    );
  };
};

const clearPipelineState = () => {
  return (dispatch: Dispatch) => {
    dispatch(success(pipelineConstant.CLEAR_PIPELINE_STATE, null));
  };
};

export const pipelineAction = {
  getDefaultPipeline,
  createStage,
  updateStage,
  deleteStage,
  updatePipeline,
  deletePipeline,
  setSelectedPipeline,
  clearPipelineState,
  updateStagesSortOrders,
};
